// utils/validationUtils.js
// Centralized validation and security utilities for all API services

// File validation restrictions
export const FILE_RESTRICTIONS = {
  image: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  },
  video: {
    maxSize: 1024 * 1024 * 1024, // 1GB
    maxDuration: 600, // 10 minutes (adjust as needed)
    allowedTypes: ['video/mp4', 'video/webm', 'video/mov', 'video/avi', 'video/quicktime'],
    allowedExtensions: ['.mp4', '.webm', '.mov', '.avi']
  },
  audio: {
    maxSize: 25 * 1024 * 1024, // 25MB
    maxDuration: 300, // 5 minutes
    allowedTypes: ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac', 'audio/webm'],
    allowedExtensions: ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm']
  },
  text: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'],
    allowedExtensions: ['.pdf', '.doc', '.docx', '.txt']
  }
};

// Input sanitization functions
export const sanitizeInput = {
  // Remove HTML tags and potential XSS
  text: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .trim()
      .substring(0, 1000); // Limit length
  },

  // Sanitize emails
  email: (input) => {
    if (typeof input !== 'string') return '';
    const sanitized = input.toLowerCase().trim();
    // Basic email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(sanitized)) {
      throw new Error('Invalid email format');
    }
    return sanitized.substring(0, 254); // RFC 5321 limit
  },

  // Sanitize passwords (don't log, just validate)
  password: (input) => {
    if (typeof input !== 'string') {
      throw new Error('Password must be a string');
    }
    if (input.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }
    if (input.length > 128) {
      throw new Error('Password is too long');
    }
    // Check for common weak patterns
    if (/^(.)\1*$/.test(input)) {
      throw new Error('Password cannot be all the same character');
    }
    return input;
  },

  // Sanitize phone numbers
  phone: (input) => {
    if (typeof input !== 'string') return '';
    return input.replace(/[^0-9+\-\s\(\)]/g, '').substring(0, 20);
  },

  // Sanitize URLs
  url: (input) => {
    if (typeof input !== 'string') return '';
    // Only allow http/https URLs
    if (!input.match(/^https?:\/\//)) {
      throw new Error('Invalid URL protocol. Only HTTP and HTTPS are allowed.');
    }
    return input.trim().substring(0, 2000);
  },

  // Sanitize file names
  filename: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace special chars with underscore
      .substring(0, 255); // Limit length
  },

  // Validate UUIDs
  uuid: (input) => {
    if (typeof input !== 'string') return '';
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(input)) {
      throw new Error('Invalid UUID format');
    }
    return input;
  },

  // Validate media types
  mediaType: (input) => {
    const allowedTypes = ['image', 'video', 'audio', 'text'];
    if (!allowedTypes.includes(input)) {
      throw new Error(`Invalid media type: ${input}`);
    }
    return input;
  },

  // Validate video types
  videoType: (input) => {
    const allowedTypes = ['bioVideo', 'topLevelVideos', 'qualification'];
    if (!allowedTypes.includes(input)) {
      throw new Error(`Invalid video type: ${input}`);
    }
    return input;
  },

  // Validate grade values
  grade: (input) => {
    if (typeof input === 'string') {
      const validGrades = ['A*', 'A', 'B', 'C', 'D', 'E', 'F', 'U', 'Pass', 'Merit', 'Distinction', 'Fail'];
      if (validGrades.includes(input)) {
        return input;
      }
    }
    if (typeof input === 'number' && input >= 0 && input <= 100) {
      return input;
    }
    throw new Error('Invalid grade format');
  },

  // Validate numeric scores
  score: (input) => {
    const num = parseFloat(input);
    if (isNaN(num) || num < 0 || num > 100) {
      throw new Error('Score must be a number between 0 and 100');
    }
    return num;
  },

  // Sanitize OTP codes
  otp: (input) => {
    if (typeof input === 'string') {
      input = parseInt(input, 10);
    }
    if (!Number.isInteger(input) || input < 100000 || input > 999999) {
      throw new Error('OTP must be a 6-digit number');
    }
    return input;
  }
};

// Rate limiting helper
const rateLimiter = new Map();
export const checkRateLimit = (action, limit = 10, timeWindow = 60000) => {
  const now = Date.now();
  const key = `${action}_${Math.floor(now / timeWindow)}`;
  
  const current = rateLimiter.get(key) || 0;
  if (current >= limit) {
    throw new Error(`Rate limit exceeded for ${action}. Please try again later.`);
  }
  
  rateLimiter.set(key, current + 1);
  
  // Clean old entries
  for (const [k] of rateLimiter) {
    if (k.split('_')[1] < Math.floor((now - timeWindow) / timeWindow)) {
      rateLimiter.delete(k);
    }
  }
};

// Authentication helper
export const validateAuth = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return token;
};

// File validation function
export const validateFileSecurely = (file) => {
  if (!file || !file.name || !file.type || typeof file.size !== 'number') {
    throw new Error('Invalid file object');
  }

  // Get file extension from name
  const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
  
  // Determine media type
  let mediaType = 'text';
  if (file.type.startsWith('image/')) mediaType = 'image';
  else if (file.type.startsWith('video/')) mediaType = 'video';
  else if (file.type.startsWith('audio/')) mediaType = 'audio';

  const restrictions = FILE_RESTRICTIONS[mediaType];
  
  // Validate file type
  if (!restrictions.allowedTypes.includes(file.type)) {
    throw new Error(`File type ${file.type} is not allowed. Allowed types: ${restrictions.allowedTypes.join(', ')}`);
  }

  // Validate file extension (double-check security)
  if (!restrictions.allowedExtensions.includes(fileExtension)) {
    throw new Error(`File extension ${fileExtension} is not allowed. Allowed extensions: ${restrictions.allowedExtensions.join(', ')}`);
  }

  // Validate file size
  if (file.size > restrictions.maxSize) {
    const maxSizeMB = (restrictions.maxSize / 1024 / 1024).toFixed(1);
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
    throw new Error(`File size ${fileSizeMB}MB exceeds maximum allowed size of ${maxSizeMB}MB`);
  }

  // Additional security checks
  if (file.size === 0) {
    throw new Error('Empty files are not allowed');
  }

  if (file.name.length > 255) {
    throw new Error('File name is too long');
  }

  return mediaType;
};

// Enhanced user data validation
export const validateUserData = (userData) => {
  const sanitized = {};
  
  // Define allowed fields and their validation
  const allowedFields = {
    firstName: 'text',
    lastName: 'text',
    email: 'email',
    phone: 'phone',
    bio: 'text',
    location: 'text',
    company: 'text',
    jobTitle: 'text',
    website: 'url',
    dateOfBirth: 'date',
    gender: 'gender',
    nationality: 'text',
    linkedIn: 'url',
    github: 'url',
    twitter: 'url'
  };
  
  for (const [field, type] of Object.entries(allowedFields)) {
    if (userData[field] !== undefined && userData[field] !== null) {
      try {
        switch (type) {
          case 'text':
            sanitized[field] = sanitizeInput.text(userData[field]);
            break;
          case 'email':
            sanitized[field] = sanitizeInput.email(userData[field]);
            break;
          case 'phone':
            sanitized[field] = sanitizeInput.phone(userData[field]);
            break;
          case 'url':
            if (userData[field]) {
              sanitized[field] = sanitizeInput.url(userData[field]);
            }
            break;
          case 'date':
            // Validate date format (YYYY-MM-DD)
            if (typeof userData[field] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(userData[field])) {
              const date = new Date(userData[field]);
              if (!isNaN(date.getTime())) {
                sanitized[field] = userData[field];
              }
            }
            break;
          case 'gender':
            // Only allow specific values
            const validGenders = ['male', 'female', 'other', 'prefer-not-to-say'];
            if (validGenders.includes(userData[field])) {
              sanitized[field] = userData[field];
            }
            break;
        }
      } catch (error) {
        console.warn(`Validation failed for field ${field}: ${error.message}`);
        // Skip invalid fields rather than throwing
      }
    }
  }
  
  return sanitized;
};

// Enhanced media content validation
export const validateMediaContent = (mediaData) => {
  const sanitized = {};
  
  // Required fields
  if (mediaData.title) {
    sanitized.title = sanitizeInput.text(mediaData.title);
    if (!sanitized.title) {
      throw new Error('Title is required and cannot be empty');
    }
  }
  
  // Optional fields
  if (mediaData.description) {
    sanitized.description = sanitizeInput.text(mediaData.description);
  }
  
  if (mediaData.comments) {
    sanitized.comments = sanitizeInput.text(mediaData.comments);
  }
  
  if (mediaData.media_path) {
    sanitized.media_path = sanitizeInput.url(mediaData.media_path);
  }
  
  if (mediaData.url) {
    sanitized.url = sanitizeInput.url(mediaData.url);
  }
  
  // Validate media type
  if (mediaData.type) {
    sanitized.type = sanitizeInput.mediaType(mediaData.type);
  }
  
  // Validate UUIDs
  if (mediaData.post_to) {
    sanitized.post_to = sanitizeInput.uuid(mediaData.post_to);
  }
  
  // Validate arrays
  if (mediaData.modules && Array.isArray(mediaData.modules)) {
    sanitized.modules = mediaData.modules.map(id => sanitizeInput.uuid(id));
  }
  
  if (mediaData.units && Array.isArray(mediaData.units)) {
    sanitized.units = mediaData.units.map(id => sanitizeInput.uuid(id));
  }
  
  // Boolean fields
  if (typeof mediaData.includeInCV === 'boolean') {
    sanitized.includeInCV = mediaData.includeInCV;
  }
  
  if (typeof mediaData.isQualification === 'boolean') {
    sanitized.isQualification = mediaData.isQualification;
  }
  
  if (typeof mediaData.isVideoGallery === 'boolean') {
    sanitized.isVideoGallery = mediaData.isVideoGallery;
  }
  
  return sanitized;
};

// Validate course data
export const validateCourseData = (courseData) => {
  const sanitized = {};
  
  // Define allowed fields and their validation
  const allowedFields = {
    course: 'text',
    name: 'text',
    title: 'text',
    description: 'text',
    level: 'text',
    awarding_body: 'text',
    duration: 'text',
    qualification_type: 'text',
    subject_area: 'text'
  };
  
  for (const [field, type] of Object.entries(allowedFields)) {
    if (courseData[field] !== undefined && courseData[field] !== null) {
      switch (type) {
        case 'text':
          sanitized[field] = sanitizeInput.text(courseData[field]);
          break;
      }
    }
  }
  
  return sanitized;
};

// Validate grade data
export const validateGradeData = (gradeData) => {
  const sanitized = {};
  
  // Required fields
  if (gradeData.qualificationId || gradeData.qualification_id) {
    sanitized.qualificationId = sanitizeInput.uuid(gradeData.qualificationId || gradeData.qualification_id);
  }
  
  if (gradeData.moduleId || gradeData.module_id) {
    sanitized.moduleId = sanitizeInput.uuid(gradeData.moduleId || gradeData.module_id);
  }
  
  if (gradeData.unitId || gradeData.unit_id) {
    sanitized.unitId = sanitizeInput.uuid(gradeData.unitId || gradeData.unit_id);
  }
  
  // Grade fields
  if (gradeData.grade) {
    sanitized.grade = sanitizeInput.grade(gradeData.grade);
  }
  
  if (gradeData.predictedGrade || gradeData.predicted_grade) {
    sanitized.predictedGrade = sanitizeInput.grade(gradeData.predictedGrade || gradeData.predicted_grade);
  }
  
  if (gradeData.currentGrade || gradeData.current_grade) {
    sanitized.currentGrade = sanitizeInput.grade(gradeData.currentGrade || gradeData.current_grade);
  }
  
  if (gradeData.targetGrade || gradeData.target_grade) {
    sanitized.targetGrade = sanitizeInput.grade(gradeData.targetGrade || gradeData.target_grade);
  }
  
  // Score fields
  if (gradeData.score !== undefined) {
    sanitized.score = sanitizeInput.score(gradeData.score);
  }
  
  if (gradeData.percentage !== undefined) {
    sanitized.percentage = sanitizeInput.score(gradeData.percentage);
  }
  
  return sanitized;
};

// Additional security function to validate bulk operations
export const validateBulkOperation = (items, maxItems = 50) => {
  if (!Array.isArray(items)) {
    throw new Error('Bulk operation data must be an array');
  }
  
  if (items.length === 0) {
    throw new Error('Bulk operation cannot be empty');
  }
  
  if (items.length > maxItems) {
    throw new Error(`Bulk operation cannot exceed ${maxItems} items`);
  }
  
  return items;
};

// Enhanced error handler
export const handleApiError = (error, operation) => {
  console.error(`Error in ${operation}:`, error);
  
  // Log additional context for debugging
  if (error.response) {
    console.error(`Response status: ${error.response.status}`);
    console.error(`Response data:`, error.response.data);
  }
  
  // Return user-friendly error messages
  if (error.message.includes('Rate limit exceeded')) {
    return new Error('Too many requests. Please wait a moment and try again.');
  }
  
  if (error.message.includes('Authentication required')) {
    return new Error('Please log in to access this feature.');
  }
  
  if (error.message.includes('Invalid UUID')) {
    return new Error('Invalid ID provided.');
  }
  
  return new Error(error.response?.data?.message || error.message || `Error in ${operation}`);
};

// Utility function to format file sizes
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Utility function to format duration
export const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Clear rate limiter (useful for testing or logout)
export const clearRateLimit = () => {
  rateLimiter.clear();
};